import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ClipboardList, Flame, Target, TrendingUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSession } from "@/stores/session";
import { useAttempts } from "@/stores/attempts";
import { useWorkspace } from "@/stores/workspace";
import { fetchAttempts, clearAttempts } from "@/lib/attempts-remote";
import { cn } from "@/lib/cn";

export function StudentDashboard() {
  const user = useSession((s) => s.user);
  const localAttempts = useAttempts((s) => s.attempts);
  const clearLocal = useAttempts((s) => s.clear);
  const setView = useWorkspace((s) => s.setView);
  const qc = useQueryClient();
  const firstName = user?.name?.split(" ")[0] ?? "there";

  // Durable store (Firestore or backend) is source of truth; local is fallback.
  const { data: serverAttempts } = useQuery({
    queryKey: ["attempts", user?.id],
    queryFn: () => fetchAttempts(user!.id),
    enabled: !!user?.id,
    retry: 0,
  });

  const attempts = serverAttempts ?? localAttempts;
  const usingServer = !!serverAttempts;
  const clear = async () => {
    if (usingServer && user?.id) {
      await clearAttempts(user.id).catch(() => undefined);
      qc.invalidateQueries({ queryKey: ["attempts", user.id] });
    }
    clearLocal();
  };

  const stats = useMemo(() => {
    if (attempts.length === 0) return null;
    const scores = attempts.map((a) => Math.round((a.estimatedLow + a.estimatedHigh) / 2));
    const latest = scores[0];
    const first = scores[scores.length - 1];
    const best = Math.max(...scores);
    const totalQ = attempts.reduce((s, a) => s + a.totalQuestions, 0);
    const totalCorrect = attempts.reduce((s, a) => s + a.totalCorrect, 0);

    // Aggregate section performance + weak-topic frequency.
    const sec: Record<string, { label: string; correct: number; total: number }> = {};
    const weak: Record<string, number> = {};
    for (const a of attempts) {
      for (const s of a.sectionScores) {
        const e = sec[s.section_id] ?? { label: s.label, correct: 0, total: 0 };
        e.correct += s.correct;
        e.total += s.total;
        sec[s.section_id] = e;
      }
      for (const t of a.weakTopics) weak[t] = (weak[t] ?? 0) + 1;
    }
    const topWeak = Object.entries(weak).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([t]) => t);
    return {
      latest, first, best, delta: latest - first,
      accuracy: totalQ ? Math.round((totalCorrect / totalQ) * 100) : 0,
      sections: Object.values(sec),
      topWeak,
      scores: [...scores].reverse(),
    };
  }, [attempts]);

  if (!stats) {
    return (
      <div className="container max-w-3xl space-y-6 py-16 text-center">
        <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" /> Dashboard
        </div>
        <h1 className="font-serif text-4xl md:text-5xl">
          No tests yet, <span className="italic text-primary">{firstName}</span>.
        </h1>
        <p className="mx-auto max-w-md text-muted-foreground">
          Take your first mock or section test and your personalized dashboard —
          score trend, section strengths, and recurring weak spots — builds itself here.
        </p>
        <Button variant="default" size="lg" onClick={() => setView("exam")}>
          Take a test <ArrowRight className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="container space-y-12 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
            <span className="hero-rule" /> Dashboard
          </div>
          <h1 className="mt-3 font-serif text-5xl leading-[0.98] tracking-tight md:text-6xl">
            {firstName}'s <span className="italic text-primary">progress</span>
          </h1>
        </div>
        <Button variant="default" onClick={() => setView("exam")}>
          <ClipboardList className="size-4" /> New test
        </Button>
      </header>

      {/* Top stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Latest estimate" value={String(stats.latest)} hint={`best ${stats.best}`} />
        <StatCard
          label="Since first test"
          value={`${stats.delta >= 0 ? "+" : ""}${stats.delta}`}
          hint="points"
          icon={<TrendingUp className="size-4 text-success" />}
        />
        <StatCard label="Overall accuracy" value={`${stats.accuracy}%`} icon={<Target className="size-4 text-primary" />} />
        <StatCard label="Tests taken" value={String(attempts.length)} icon={<Flame className="size-4 text-accent" />} />
      </div>

      {/* Score trend */}
      <section className="space-y-4">
        <h2 className="font-serif text-2xl">Score trend</h2>
        <div className="rounded-2xl border border-border/60 bg-card/60 p-6">
          <Sparkline values={stats.scores} />
        </div>
      </section>

      {/* Section performance */}
      <section className="space-y-4">
        <h2 className="font-serif text-2xl">Section performance</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {stats.sections.map((s) => {
            const pct = s.total ? Math.round((s.correct / s.total) * 100) : 0;
            return (
              <div key={s.label} className="rounded-2xl border border-border/60 bg-card/60 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{s.label}</span>
                  <span className="tabular-nums text-muted-foreground">{pct}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div
                    className={cn("h-full", pct < 50 ? "bg-destructive" : pct < 75 ? "bg-accent" : "bg-success")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {s.correct}/{s.total} across all tests
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recurring weak spots */}
      {stats.topWeak.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-serif text-2xl">Recurring weak spots</h2>
          <div className="flex flex-wrap gap-2">
            {stats.topWeak.map((t) => (
              <button
                key={t}
                onClick={() => setView("tutor")}
                className="rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Tap a topic to work it with Maya.</p>
        </section>
      )}

      {/* History */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl">Test history</h2>
          <button
            onClick={clear}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="size-3.5" /> Clear
          </button>
        </div>
        <div className="space-y-2">
          {attempts.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 px-4 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{a.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {new Date(a.date).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  {" · "}
                  {a.totalCorrect}/{a.totalQuestions}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="tabular-nums">
                  ~{Math.round((a.estimatedLow + a.estimatedHigh) / 2)}
                </Badge>
                <span className="text-sm tabular-nums text-muted-foreground">{a.overallPercent}%</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="grain rounded-2xl border border-border/60 bg-card/70 p-5 shadow-editorial">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
        {icon}
      </div>
      <div className="mt-2 font-serif text-4xl tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        Take another test to see your trend line.
      </div>
    );
  }
  const w = 640;
  const h = 120;
  const min = Math.min(...values) - 10;
  const max = Math.max(...values) + 10;
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return [x, y] as const;
  });
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${d} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-32 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="trend" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="hsl(var(--primary))" stopOpacity="0.25" />
          <stop offset="1" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#trend)" />
      <path d={d} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill="hsl(var(--primary))" />
      ))}
    </svg>
  );
}
